/**
 * Webhook-failures API client + wire-contract types — #935 PR-D.
 *
 * Repointed from the archived webhook-failures surface to
 * the rebuilt backend's `/api/webhook-failures/*` (composition root:
 * `backend/src/platform/webhook/failure-routes.ts`):
 *
 *   GET  /api/webhook-failures                 list (operator inbox)
 *   GET  /api/webhook-failures/count           OPEN-failure badge count
 *   GET  /api/webhook-failures/by-trace/{id}   failures for one trace
 *   GET  /api/webhook-failures/{id}            detail (+ payload snapshot)
 *   POST /api/webhook-failures/{id}/resolve    operator confirms handled
 *   POST /api/webhook-failures/bulk-resolve    close many
 *   POST /api/webhook-failures/{id}/replay     replay through the live core
 *
 * Reads are `webhook_failures:read`; resolve / bulk-resolve / replay are
 * `webhook_failures:write` and auto-audited server-side (G8). `http.get`
 * returns the UNWRAPPED inner `data` (#935-A) so the list caller reads
 * `.items` / `.total` and the count caller reads `.count`.
 *
 * Item + result shapes come from the generated OpenAPI types (ADR-0006).
 * `ERROR_KIND` / `PROVIDER` are the closed-set SOT the UI derives badge
 * tables from — a backend rename fails compilation at the `Record`
 * exhaustiveness check rather than rendering a raw enum string.
 */

import { http } from '@/lib/api';
import type { components } from '@/types/api.generated';
import { toApiListParams } from './list-request';
import { WEBHOOK_FAILURE_FILTERS } from './filters.generated';

// The `/api/webhook-failures` filter whitelist is codegen from the backend
// `FAILURE_LIST_CONFIG.filterable` via the OpenAPI `x-filterable` extension
// (issue #1407). Re-exported here so the `@/services/webhook-failures`
// import path is unchanged; edit the backend config, not this file.
export { WEBHOOK_FAILURE_FILTERS };

export type WebhookFailureListItem = components['schemas']['WebhookFailureListItem'];
export type WebhookFailureDetail = components['schemas']['WebhookFailureDetail'];
export type WebhookFailureResolveResult = components['schemas']['WebhookFailureResolveResult'];
export type WebhookFailureBulkResolveResult =
  components['schemas']['WebhookFailureBulkResolveResult'];
export type WebhookFailureReplayResult = components['schemas']['WebhookFailureReplayResult'];
export type WebhookFailureDeleteResult = components['schemas']['WebhookFailureDeleteResult'];
export type WebhookFailureBulkDeleteResult =
  components['schemas']['WebhookFailureBulkDeleteResult'];
export type WebhookFailurePurgeResult = components['schemas']['WebhookFailurePurgeResult'];

/**
 * `webhook_failures:write` — the grant the backend requires for
 * resolve / bulk-resolve / replay (`PERMISSIONS.WEBHOOK_FAILURES_WRITE`
 * in `backend/src/platform/auth/permissions.ts`). Mirrored here as a
 * closed const because the frontend `src/permissions.ts` catalogue does
 * not yet carry the inbox grants (that migration rides with the PR-E IA
 * restructure); the replay button reads the user's permission set
 * against this value rather than hardcoding a role check.
 */
export const WEBHOOK_FAILURES_WRITE_PERMISSION = 'webhook_failures:write' as const;

/**
 * The two closed sets moved to `./webhook-failures.constants.ts` in #1805 A3b
 * so the badge tables in `features/admin/components/webhookFailureBadges.ts` —
 * now reached from `features/admin/widgets.tsx` — can read them without
 * dragging `@/lib/api` into every test suite (see that file's header). They are
 * re-exported here so the `@/services/webhook-failures` import path is
 * unchanged; edit the constants module, not this file.
 */
export { ERROR_KIND, PROVIDER } from './webhook-failures.constants';
export type { ErrorKind, Provider } from './webhook-failures.constants';

export interface WebhookFailureListResponse {
  items: WebhookFailureListItem[];
  total: number;
  page: number;
  size: number;
}

export interface WebhookFailureCountResponse {
  count: number;
}

export interface WebhookFailureByTraceResponse {
  items: WebhookFailureListItem[];
}

export interface ListParams {
  /** 1-based page number (shared list kernel). */
  page?: number;
  /** Page size; backend clamps to floor 1 / ceiling 100. */
  size?: number;
  sort?: string;
  /**
   * Retained for the shared `ListParams` shape but NEVER forwarded — the
   * webhook-failures list route has no `search` column, so a free-text term is
   * dropped rather than 400ing the whole page.
   */
  search?: string;
  /**
   * Whitelisted list-kernel filters, pre-serialized to the
   * `<field>=<op>:<value>` wire form via `serializeConditionsToParams`. Each
   * entry is whitelisted against `WEBHOOK_FAILURE_FILTERS` and appended to the
   * query; an out-of-whitelist field/op is dropped (#1154).
   */
  extraParams?: Record<string, string>;
}

export const webhookFailuresAPI = {
  async list(params: ListParams = {}): Promise<WebhookFailureListResponse> {
    const search = new URLSearchParams();
    if (params.page != null) search.set('page', String(params.page));
    if (params.size != null) search.set('size', String(params.size));
    if (params.sort) search.set('sort', params.sort);
    // Whitelist the operator's serialized conditions against the backend
    // filterable map via the shared list kernel. An unsupported field/op is
    // dropped rather than 400ing the whole page; `search` is not forwarded.
    const filters = toApiListParams({ extraParams: params.extraParams }, WEBHOOK_FAILURE_FILTERS);
    for (const [field, value] of Object.entries(filters)) {
      search.set(field, String(value));
    }
    // Inbox semantics (#1235): the failure queue lists UNRESOLVED rows only by
    // DEFAULT, matching the OPEN-only `/count` badge — a resolved failure must
    // never render as replayable. The shared list kernel parses a bare value as
    // an `eq` literal, so `resolved=false` maps to the backend's
    // `resolved: ["eq"]` filter. But if the operator built an explicit
    // `resolved` filter it already sits in `search` above, so respect it and do
    // NOT append a second, conflicting `resolved` param.
    if (!search.has('resolved')) search.set('resolved', 'false');
    const qs = search.toString();
    return http.get<WebhookFailureListResponse>(`/api/webhook-failures${qs ? `?${qs}` : ''}`);
  },

  async count(): Promise<WebhookFailureCountResponse> {
    return http.get<WebhookFailureCountResponse>('/api/webhook-failures/count');
  },

  async getById(id: string): Promise<WebhookFailureDetail> {
    return http.get<WebhookFailureDetail>(`/api/webhook-failures/${id}`);
  },

  async listByTrace(traceId: string): Promise<WebhookFailureByTraceResponse> {
    return http.get<WebhookFailureByTraceResponse>(`/api/webhook-failures/by-trace/${traceId}`);
  },

  async resolve(id: string): Promise<WebhookFailureResolveResult> {
    return http.post<WebhookFailureResolveResult>(`/api/webhook-failures/${id}/resolve`, {});
  },

  async bulkResolve(ids: string[]): Promise<WebhookFailureBulkResolveResult> {
    return http.post<WebhookFailureBulkResolveResult>('/api/webhook-failures/bulk-resolve', {
      ids,
    });
  },

  async replay(id: string): Promise<WebhookFailureReplayResult> {
    return http.post<WebhookFailureReplayResult>(`/api/webhook-failures/${id}/replay`, {});
  },

  async deleteById(id: string): Promise<WebhookFailureDeleteResult> {
    return http.delete<WebhookFailureDeleteResult>(`/api/webhook-failures/${id}`);
  },

  async bulkDelete(ids: string[]): Promise<WebhookFailureBulkDeleteResult> {
    return http.post<WebhookFailureBulkDeleteResult>('/api/webhook-failures/bulk-delete', { ids });
  },

  // `purge` with no `before` drops every failure row wholesale (the "clear
  // all" affordance). A `before` cutoff is accepted by the backend but the
  // inbox surfaces only the wholesale drop today.
  async purge(before?: string): Promise<WebhookFailurePurgeResult> {
    return http.post<WebhookFailurePurgeResult>(
      '/api/webhook-failures/purge',
      before ? { before } : {},
    );
  },
};

/**
 * The backend `bulk-delete` chunks under the D1 100-parameter ceiling
 * (#1363), but the UI still caps a single bulk action to keep the confirm
 * copy honest and the request bounded. The list page size ceiling is 100
 * (default 50), so a page-local selection never exceeds this — the cap is a
 * belt-and-suspenders bound enforced at the call site (`handleBulkDelete`
 * slices to it and the bulk button warns when a selection would be truncated).
 */
export const MAX_BULK_DELETE = 100;
